import React, { ReactNode, useMemo, useRef } from 'react';
import { Col, FormCheck, Row } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import { FormGroup } from './FormGroup';
import InputError from './InputError';
import InputLabel from './InputLabel';
import { InfoHint } from '../hint/InfoHint';
import ScrollToError from './ScrollToError';
import { getError } from './utils';

type InputType = 'checkbox';

type Props = InputProps;

type InputProps = {
  label?: string;
  name: string;
  placeholder?: string;
  type?: InputType;
  children?: ReactNode;
  disabled?: boolean;
  required?: boolean;
  labelComponent?: ReactNode;
  autoFocus?: boolean;
  size?: 'sm' | 'lg';
  className?: string;
  hint?: ReactNode;
  onChange?: (value: boolean) => void;
};

type ControlledInputProps = Omit<
  InputProps,
  'label' | 'labelComponent' | 'required'
>;

export const ControlledCheckboxInput: React.FC<
  React.PropsWithChildren<ControlledInputProps>
> = ({ name, disabled, className, onChange, placeholder, autoFocus }) => {
  const {
    control,
    formState: { errors, isSubmitting },
  } = useFormContext();

  const error = getError(name, errors);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const { value, onChange: setFieldValue, ...fieldProps } = field;

        return (
          <FormCheck.Input
            {...fieldProps}
            checked={!!value}
            onChange={(event) => {
              setFieldValue(event.target.checked);
              onChange?.(event.target.checked);
            }}
            disabled={disabled}
            isInvalid={!!error}
            readOnly={isSubmitting}
            type="checkbox"
            className={className}
            placeholder={placeholder}
            autoFocus={autoFocus}
          />
        );
      }}
    />
  );
};

export const CheckboxInput: React.FC<React.PropsWithChildren<Props>> = ({
  label,
  name,
  required,
  hint,
  labelComponent,
  ...rest
}) => {
  const ref = useRef(null);
  const {
    formState: { errors },
  } = useFormContext();

  const error = useMemo(() => getError(name, errors), [name, errors]);

  return React.useMemo(
    () => (
      <FormGroup ref={ref}>
        <Row>
          <Col>
            <InputLabel
              label={label}
              required={required}
              hint={
                hint ? <InfoHint className="mt-2" text={hint} /> : undefined
              }
              className="form-check-label"
            />
          </Col>

          <Col className="col-auto">
            <ControlledCheckboxInput name={name} {...rest} />
          </Col>
        </Row>
        {error && (
          <Row>
            <Col>
              <InputError error={error} className="d-block" />
            </Col>
          </Row>
        )}
        <ScrollToError name={name} inputContainerRef={ref} />
      </FormGroup>
    ),
    [label, name, required, rest, ref, hint, error],
  );
};
